import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Loader from '../Loader/Loader';
import './LLD.css';
import {getRequest, postRequest} from "../../Fetch/request";
import { useAuth } from '../../hooks/useAuth';
import DesignPatternQuestions from './DesignPatternQuestions';
import ClassDiagramDraw from '../ClassDiagram/ClassDiagramDraw';
import Explorer from '../LldEditor/Explorer';
import TabsBar from '../LldEditor/TabsBar';
import EditorPane from '../LldEditor/EditorPane';
import OutputPanel from '../LldEditor/OutputPanel';
import { EditorProvider } from '../context/EditorProvider';
import LLDDocument from './LLDDocument';
import ClarificationPanel from '../Common/ClarificationPanel';
import SchemaDesigner from '../SchemaDesigner/SchemaDesigner';
import CombinedDiagramDraw from '../CombinedDiagram/CombinedDiagramDraw';
import Features from './Features';
import DraggableNotes from '../Common/DraggableNotes';
import { LLD_STEPS, UI_TEXT, STORAGE_KEYS, API_ENDPOINTS, DEFAULTS, VALIDATION, STEP_NAME_BY_NUMBER } from '../../Utils/constants';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
import { CODE_EVALUATION_PROMPT } from '../../Utils/aiPrompts';
import { CODE_EVALUATION_SCHEMA } from '../../Utils/aiSchemas';
import EvaluationFeedbackPanel from '../Common/EvaluationFeedbackPanel';
import { normalizeDiagramFeedback } from '../../Utils/diagramFeedbackUtils';
import { addLearningPointsToNotes } from '../../Utils/learningPointsUtils';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Avatar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack,
  Chat,
  Person,
  CheckCircle,
  RadioButtonUnchecked,
  Description,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';





const STEPS = LLD_STEPS;

export default function LLDProblemDetails(){
  const navigate = useNavigate();
  const loc = useLocation();
  const problemId = new URLSearchParams(loc.search).get('problemId');
  const { isAuthenticated } = useAuth();


  const [problem,setProblem] = useState(null);
  const [loading,setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [features,setFeatures] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState([]); // [{ pattern, usage }]
  const [entities,setEntities] = useState([]);
  const [schemaDesign,setSchemaDesign] = useState({ entities: [], relationships: [] });
  const [clarifications, setClarifications] = useState([]); // Add clarifications state
  const [editorVisible,setEditorVisible] = useState(true);
  const [umlData, setUmlData] = useState({ classes: [], connections: [] });
  const [umlDataTimestamp, setUmlDataTimestamp] = useState(Date.now());
  const [documentVisible, setDocumentVisible] = useState(false);
  
  // Track unsaved changes from diagram components
  const [diagramUnsavedChanges, setDiagramUnsavedChanges] = useState(false);
  
  // Code evaluation state
  const [isEvaluatingCode, setIsEvaluatingCode] = useState(false);
  const [codeEvaluationFeedback, setCodeEvaluationFeedback] = useState(null);
  
  // Sidebar state management
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(`lld-sidebar-width-${problemId}`);
    return savedWidth ? parseInt(savedWidth) : 350; // Default width
  });
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    const savedVisibility = localStorage.getItem(`lld-sidebar-visible-${problemId}`);
    return savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);
  
  // Right sidebar state management
  const [rightSidebarVisible, setRightSidebarVisible] = useState(() => {
    const savedVisibility = localStorage.getItem(`lld-right-sidebar-visible-${problemId}`);
    return savedVisibility ? JSON.parse(savedVisibility) : true; // Default visible
  });
  const [rightSidebarWidth, setRightSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(`lld-right-sidebar-width-${problemId}`);
    return savedWidth ? parseInt(savedWidth) : 400; // Default width
  });
  
  // Initialize step from localStorage or default to 0
  const [step, setStep] = useState(() => {
    const savedStep = localStorage.getItem(`lld-step-${problemId}`);
    const initialStep = savedStep ? parseInt(savedStep) : 0;
    return initialStep;
  });

  // Update step and save to localStorage - data is already saved in localStorage
  const setStepAndSave = (newStep) => {
    // Don't allow navigation if already on the target step
    if (newStep === step) {
      return;
    }
    
    // Block navigation if there are unsaved changes in the code editor
    if (step === 3 && newStep !== 3) {
      // We're leaving the code editor step
      const editorState = JSON.parse(localStorage.getItem(`lld-editor-files-${problemId}`) || '{}');
      // Check if there are unsaved changes and if the code was recently run (which would have saved it)
      const lastRunTime = localStorage.getItem(`lld-last-run-${problemId}`);
      const currentTime = Date.now();
      const recentlyRun = lastRunTime && (currentTime - parseInt(lastRunTime)) < 60000; // Within 1 minute
      const hasUnsavedChanges = editorState.hasUnsavedChanges && !recentlyRun;
      
      if (hasUnsavedChanges) {
        alert('You have not saved your code. Please save your changes before moving to another step.');
        return; // Block step change
      }
    }
    
    setStep(newStep);
    localStorage.setItem(`lld-step-${problemId}`, newStep.toString());
  };
  
  // Handle code evaluation
  const handleCodeEvaluate = useCallback(async () => {
    if (isEvaluatingCode) return;
    
    setIsEvaluatingCode(true);
    setCodeEvaluationFeedback(null);
    
    try {
      // Get code files from localStorage
      const editorData = JSON.parse(localStorage.getItem(`lld-editor-files-${problemId}`) || '{}');
      const codeFiles = editorData.files || [];
      
      if (codeFiles.length === 0 || codeFiles.every(f => !f.content || f.content.trim() === '')) {
        alert('Please write some code before evaluating.');
        setIsEvaluatingCode(false);
        return;
      }
      
      // Get problem title and features
      const attemptKey = `lld-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      const problemKey = `lld-problem-${problemId}`;
      const problemData = JSON.parse(localStorage.getItem(problemKey) || '{}');
      const problemTitle = problemData.title || 'System Design Problem';
      const features = attemptData.features || problemData.features || [];
      
      // Get userId
      const userId = localStorage.getItem('userId') || 
        (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 
        'slazysloth_user';
      
      // Prepare prompt - only pass problem title, features, and code files
      const prompt = CODE_EVALUATION_PROMPT(
        problemTitle,
        features,
        codeFiles
      );
      
      // Call AI evaluation
      const response = await getAIFeedbackSync(
        prompt,
        `Evaluate the complete code implementation.`,
        userId,
        'anthropic',
        null,
        CODE_EVALUATION_SCHEMA,
        {
          maxRetries: 3,
          onRetry: (attempt, error, delay) => {
            console.log(`Retry attempt ${attempt} after ${delay}ms`);
          }
        }
      );
      
      // Parse response
      let parsedFeedback = response;
      if (typeof response === 'string') {
        try {
          parsedFeedback = JSON.parse(response);
        } catch (e) {
          parsedFeedback = { assessment: response };
        }
      }
      
      // Handle nested API response structure
      if (parsedFeedback && typeof parsedFeedback === 'object') {
        if (parsedFeedback.success && parsedFeedback.data && parsedFeedback.data.response) {
          parsedFeedback = parsedFeedback.data.response;
        } else if (parsedFeedback.data && parsedFeedback.data.assessment) {
          parsedFeedback = parsedFeedback.data;
        }
      }
      
      // Normalize feedback
      const normalizedFeedback = normalizeDiagramFeedback(parsedFeedback);
      setCodeEvaluationFeedback(normalizedFeedback);
      
      // Add learning points to notes
      if (normalizedFeedback.learning) {
        await addLearningPointsToNotes(normalizedFeedback, problemId, 'LLD');
      }
      
    } catch (error) {
      console.error('Code evaluation error:', error);
      setCodeEvaluationFeedback({
        assessment: 'Failed to evaluate code. Please try again.',
        mistakes: '',
        strengths: '',
        learning: ''
      });
    } finally {
      setIsEvaluatingCode(false);
    }
  }, [problemId, isEvaluatingCode]);

  const nonEmpty = arr => arr.filter(i=>i.text&&i.text.trim()).length;
  // Features come from problem data, so we don't need to check them for readiness
  // Design pattern questions are now validated directly, no need for patterns check
  const ready = true; // Always ready since we're using direct validation

  const updateMetadata = async (next = {}, retryCount = 0) => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
    if (!userId || !problemId) {
      throw new Error("Missing user id or problem ID");
    }
    
    // Only send fields that have been updated (present in 'next' parameter)
    const dataToSend = {
      userId: userId,
      lldProblemId: problemId
    };
    
    // Only include fields that are in 'next' (the fields that were actually updated)
    if (next.features !== undefined) {
      dataToSend.features = Array.isArray(next.features) ? next.features : [];
    }
    
    if (next.codeFiles !== undefined) {
      dataToSend.codeFiles = Array.isArray(next.codeFiles) ? next.codeFiles : [];
    }
    
    if (next.clarifications !== undefined) {
      dataToSend.clarifications = Array.isArray(next.clarifications) ? next.clarifications : [];
    }
    
    if (next.designPatterns !== undefined) {
      dataToSend.designPatterns = next.designPatterns;
    }
    
    // If no fields to update, return early
    if (Object.keys(dataToSend).length === 2) { // Only userId and lldProblemId
      return { status: '200', message: 'No fields to update' };
    }
    
    try {
      const response = await postRequest(
        process.env.REACT_APP_URL + '/lld-attempt/update-metadata',
        dataToSend
      );
      if (!response || response.status !== '200') {
        throw new Error(`Invalid response: ${JSON.stringify(response)}`);
      }
      return response;
    } catch (err) {
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return updateMetadata(next, retryCount + 1);
      }
      throw err;
    }
  };

  // Debounced update function to avoid too many API calls
  const debouncedUpdate = (() => {
    let timeoutId;
    return (data) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          const result = await updateMetadata(data);
        } catch (error) {
          // Error is already handled by the blocking logic in request.js
          // which shows the login prompt. Just silently catch here.
        }
      }, 1000); // 1 second debounce
    };
  })();

  const handlePatternsChange = useCallback((patterns) => {
    setSelectedPatterns(patterns);
    const attemptKey = `lld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    currentData.designPatterns = patterns;
    currentData.timestamp = Date.now();
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    debouncedUpdate({ designPatterns: patterns });
  }, [problemId]);

  const setEntitiesAndUpdate = useCallback((val) => {
    setEntities(val);
    // Update localStorage immediately
    const attemptKey = `lld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    currentData.entities = val;
    currentData.timestamp = Date.now(); // Update timestamp
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    
    // NOTE: Entities are saved via update-drawing API in SchemaDesigner component
    // This function only updates localStorage. The actual save happens in SchemaDesigner.saveSchema()
    // which calls /lld-attempt/update-drawing endpoint
    // DO NOT call metadata API for entities - use drawing API only
  }, [problemId]);

  const setSchemaDesignAndUpdate = useCallback(async (val) => {
    setSchemaDesign(val);
    // Update localStorage immediately - use flat structure
    const attemptKey = `lld-attempt-${problemId}`;
    const currentData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
    
    // Store in flat structure (not nested in schemaDesign)
    if (val && val.entities) {
      currentData.schemaEntities = val.entities;
      currentData.schemaRelationships = val.relationships || [];
    }
    currentData.timestamp = Date.now(); // Update timestamp
    localStorage.setItem(attemptKey, JSON.stringify(currentData));
    
    // NOTE: Entities are saved via update-drawing API in SchemaDesigner component
    // This function only updates localStorage. The actual save happens in SchemaDesigner.saveSchema()
    // which calls /lld-attempt/update-drawing endpoint
  }, [problemId]);

  const handleClarificationsChange = useCallback((clarifications) => {
    // Trigger metadata update with clarifications
    debouncedUpdate({ clarifications: clarifications });
  }, [debouncedUpdate]);

  // Function to update UML data locally (no API call)
  const setUmlDataOnly = (val) => {
    setUmlData(val);
  };

  // Simple data loading function - fetch all data first, then render
  const loadAllData = async () => {
    const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';

    if (!problemId) {
      setLoading(false);
      return;
    }

    try {
      // Make all API calls in parallel and wait for ALL to complete
      const [problemRes, aiStepsRes, attemptRes] = await Promise.all([
        getRequest(`${process.env.REACT_APP_URL}/lld-problems/${problemId}`),
        isAuthenticated ? getRequest(`${process.env.REACT_APP_URL}/ai-answers/lld-step/all?user_id=${encodeURIComponent(userId)}&lld_problem_id=${encodeURIComponent(problemId)}`) : Promise.resolve(null),
        isAuthenticated ? getRequest(`${process.env.REACT_APP_URL}/lld-attempt?userId=${userId}&lldProblemId=${problemId}`) : Promise.resolve(null)
      ]);

      // Process problem data
      let loadedProblem = null;
      if (problemRes?.status === '200' && problemRes?.response) {
        loadedProblem = problemRes.response;
        setProblem(loadedProblem);
        // Store problem data in localStorage for faster access on refresh
        localStorage.setItem(`lld-problem-${problemId}`, JSON.stringify(loadedProblem));
      } else {
        // Fallback to list endpoint
        const allProblemsRes = await getRequest(`${process.env.REACT_APP_URL}/lld-problems?userId=${userId}`);
        if (allProblemsRes?.status === '200' && Array.isArray(allProblemsRes.response)) {
          const foundProblem = allProblemsRes.response.find(p => p.problemId === problemId);
          if (foundProblem) {
            loadedProblem = foundProblem;
            setProblem(foundProblem);
            // Store problem data in localStorage for faster access on refresh
            localStorage.setItem(`lld-problem-${problemId}`, JSON.stringify(foundProblem));
          }
        }
      }

      // Process AI feedback data and store in localStorage
      if (aiStepsRes?.status === '200' && Array.isArray(aiStepsRes.response)) {
        const stepNameByNumber = STEP_NAME_BY_NUMBER.LLD;
        aiStepsRes.response.forEach((row) => {
          const stepName = stepNameByNumber[row.step_number];
          if (!stepName) return;
          const storageKey = `ai-feedback-LLD-${problemId}-${stepName}`;
          const fromServer = ((row.ai_answers || []).slice()).map((ans, idx) => ({
            id: `${row.id || 'srv'}_${idx}`,
            content: ans,
            timestamp: row.updated_date || row.created_date || new Date().toISOString()
          }));
          localStorage.setItem(storageKey, JSON.stringify(fromServer));
        });
      }

      // Process attempt data and store in localStorage
      const attemptKey = `lld-attempt-${problemId}`;
      
      // Overwrite localStorage with backend data on refresh/initial load
      // Strategy: Replace localStorage completely with backend data (same as HLD)
      // If backend has no data, preserve local data to avoid losing user work
      if (attemptRes?.status === '200' && attemptRes.response) {
        const at = attemptRes.response;
        
        // Overwrite localStorage with backend data (use actual backend values, not defaults)
        // Check both schemaDesign.entities and entities fields, prefer schemaDesign.entities
        const backendEntities = at.schemaDesign?.entities ?? at.schemaEntities ?? at.entities ?? [];
        
        // Extract features from problem data if attempt doesn't have them
        let featuresToStore = at.features ?? [];
        if ((!featuresToStore || featuresToStore.length === 0) && loadedProblem) {
          const problemDetails = loadedProblem.problemDetails || loadedProblem;
          const problemFeatures = problemDetails.features || loadedProblem.features || [];
          if (problemFeatures.length > 0) {
            featuresToStore = problemFeatures.map(f => typeof f === 'string' ? f : f.text || f);
          }
        }
        
        const attemptDataToStore = {
          clarifications: at.clarifications ?? [],
          features: featuresToStore,
          designPatterns: at.designPatterns ?? [],
          entities: at.entities ?? [],
          // Convert schemaDesign to flat structure if it exists
          // Store entities in schemaEntities for SchemaDesigner to use
          // If backend has entities but no schemaEntities, copy entities to schemaEntities
          schemaEntities: backendEntities.length > 0 ? backendEntities : (at.entities && at.entities.length > 0 ? at.entities : []),
          schemaRelationships: at.schemaDesign?.relationships ?? at.schemaRelationships ?? [],
          editorVisible: at.isEditorVisible !== undefined ? at.isEditorVisible : true,
          classBoxes: at.classBoxes ?? [],
          classDiagram: at.classDiagram ?? {},
          codeFiles: at.codeFiles ?? [],
          timestamp: Date.now()
        };

        // Preserve diagram snapshots — they are captured locally and never stored on the backend
        const existingLocalForSnapshot = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        if (existingLocalForSnapshot.schemaSnapshot) attemptDataToStore.schemaSnapshot = existingLocalForSnapshot.schemaSnapshot;
        if (existingLocalForSnapshot.classSnapshot) attemptDataToStore.classSnapshot = existingLocalForSnapshot.classSnapshot;

        // Overwrite localStorage with backend data
        localStorage.setItem(attemptKey, JSON.stringify(attemptDataToStore));
        
        // Dispatch custom event to notify child components that localStorage was updated
        window.dispatchEvent(new CustomEvent('localStorageUpdated', { 
          detail: { key: attemptKey, timestamp: attemptDataToStore.timestamp } 
        }));

        // Set state from backend data
        setEditorVisible(attemptDataToStore.editorVisible);
        setClarifications(attemptDataToStore.clarifications);
        // Set features from attempt data if available (they might come from backend or problem data)
        if (attemptDataToStore.features && attemptDataToStore.features.length > 0) {
          setFeatures(attemptDataToStore.features);
        }
        // Features will also be set from problem data in the useEffect if problem data has features
        setSelectedPatterns(attemptDataToStore.designPatterns || []);
        setEntities(attemptDataToStore.entities);
        // Convert flat structure back to schemaDesign format for state
        // Check both schemaEntities and entities fields (entities might be stored in either)
        const entitiesToUse = attemptDataToStore.schemaEntities && attemptDataToStore.schemaEntities.length > 0
          ? attemptDataToStore.schemaEntities
          : (attemptDataToStore.entities && attemptDataToStore.entities.length > 0
            ? attemptDataToStore.entities.map(entity => {
                // Convert entities with fields array to content string format if needed
                if (entity.content !== undefined) {
                  return { 
                    ...entity, 
                    content: entity.content || '',
                    headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                  };
                }
                // Convert fields array to content string
                if (entity.fields && Array.isArray(entity.fields)) {
                  const fieldsStr = entity.fields.map(field => {
                    if (typeof field === 'string') return field;
                    if (field && typeof field === 'object') {
                      return field.name || field.text || '';
                    }
                    return '';
                  }).filter(f => f.trim()).join('\n');
                  return { 
                    ...entity, 
                    content: fieldsStr,
                    headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                  };
                }
                return { 
                  ...entity, 
                  content: '',
                  headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                };
              })
            : []);
        setSchemaDesign({
          entities: entitiesToUse,
          relationships: attemptDataToStore.schemaRelationships || []
        });
      } else {
        // If no backend data (404, error, or empty response), check if we have local data
        // Don't clear localStorage if we have existing local data - preserve user work
        const existingLocalData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        
        // Extract features from problem data if available
        let featuresFromProblem = [];
        if (loadedProblem) {
          const problemDetails = loadedProblem.problemDetails || loadedProblem;
          const problemFeatures = problemDetails.features || loadedProblem.features || [];
          if (problemFeatures.length > 0) {
            featuresFromProblem = problemFeatures.map(f => typeof f === 'string' ? f : f.text || f);
          }
        }
        
        const hasLocalData = existingLocalData && (
          (existingLocalData.clarifications && existingLocalData.clarifications.length > 0) ||
          (existingLocalData.features && existingLocalData.features.length > 0) ||
          (existingLocalData.designPatterns && existingLocalData.designPatterns.length > 0) ||
          (existingLocalData.entities && existingLocalData.entities.length > 0) ||
          (existingLocalData.schemaEntities && existingLocalData.schemaEntities.length > 0) ||
          (existingLocalData.codeFiles && existingLocalData.codeFiles.length > 0)
        );
        
        if (!hasLocalData) {
          // Only clear localStorage if there's no local data to preserve
          // But include features from problem data if available
          const emptyAttemptData = {
            clarifications: [],
            features: featuresFromProblem, // Include features from problem data
            designPatterns: [],
            entities: [],
            schemaEntities: [],
            schemaRelationships: [],
            editorVisible: true,
            classBoxes: [],
            classDiagram: {},
            codeFiles: [],
            timestamp: Date.now()
          };

          // Preserve diagram snapshots even when clearing other data
          if (existingLocalData.schemaSnapshot) emptyAttemptData.schemaSnapshot = existingLocalData.schemaSnapshot;
          if (existingLocalData.classSnapshot) emptyAttemptData.classSnapshot = existingLocalData.classSnapshot;

          // Clear localStorage by storing empty structure
          localStorage.setItem(attemptKey, JSON.stringify(emptyAttemptData));
          
          // Dispatch custom event to notify child components that localStorage was updated
          window.dispatchEvent(new CustomEvent('localStorageUpdated', { 
            detail: { key: attemptKey, timestamp: emptyAttemptData.timestamp } 
          }));
          
          // Set state to empty values only if no local data, but include features from problem
          setEditorVisible(true);
          setClarifications([]);
          if (featuresFromProblem.length > 0) {
            setFeatures(featuresFromProblem);
          } else {
            setFeatures([]);
          }
          setEntities([]);
          setSchemaDesign({
            entities: [],
            relationships: []
          });
        } else {
          // If we have local data, preserve it and set state from local data
          // Merge features from problem data if local data doesn't have features
          if (featuresFromProblem.length > 0 && (!existingLocalData.features || existingLocalData.features.length === 0)) {
            existingLocalData.features = featuresFromProblem;
            existingLocalData.timestamp = Date.now();
            localStorage.setItem(attemptKey, JSON.stringify(existingLocalData));
          }
          
          // Set state from preserved local data
          setEditorVisible(existingLocalData.editorVisible !== undefined ? existingLocalData.editorVisible : true);
          setClarifications(existingLocalData.clarifications || []);
          setFeatures(existingLocalData.features || featuresFromProblem || []);
          setSelectedPatterns(existingLocalData.designPatterns || []);
          setEntities(existingLocalData.entities || []);
          
          // Convert flat structure back to schemaDesign format for state
          const localEntitiesToUse = existingLocalData.schemaEntities && existingLocalData.schemaEntities.length > 0
            ? existingLocalData.schemaEntities
            : (existingLocalData.entities && existingLocalData.entities.length > 0
              ? existingLocalData.entities.map(entity => {
                  // Convert entities with fields array to content string format if needed
                  if (entity.content !== undefined) {
                    return { 
                      ...entity, 
                      content: entity.content || '',
                      headerColor: entity.headerColor || '#374151'
                    };
                  }
                  // Convert fields array to content string
                  if (entity.fields && Array.isArray(entity.fields)) {
                    const fieldsStr = entity.fields.map(field => {
                      if (typeof field === 'string') return field;
                      if (field && typeof field === 'object') {
                        return field.name || field.text || '';
                      }
                      return '';
                    }).filter(f => f.trim()).join('\n');
                    return { 
                      ...entity, 
                      content: fieldsStr,
                      headerColor: entity.headerColor || '#374151'
                    };
                  }
                  return { 
                    ...entity, 
                    content: '',
                    headerColor: entity.headerColor || '#374151'
                  };
                })
              : []);
          setSchemaDesign({
            entities: localEntitiesToUse,
            relationships: existingLocalData.schemaRelationships || []
          });
        }
        // If we have local data, keep it and don't clear - preserve user work
      }

      setDataLoaded(true);
    } catch (error) {
      console.error('❌ Error loading LLD data:', error);
    } finally {
      // Only set loading to false after ALL data is processed
      setLoading(false);
    }
  };

  // Load data from localStorage immediately on mount (for instant display)
  useEffect(() => {
    if (!problemId) {
      setLoading(false);
      return;
    }

    // Load from localStorage immediately for instant display
    const attemptKey = `lld-attempt-${problemId}`;
    const localAttemptData = localStorage.getItem(attemptKey);
    
    if (localAttemptData) {
      try {
        const parsed = JSON.parse(localAttemptData);
        
        // Set state from localStorage immediately
        if (parsed.clarifications) setClarifications(parsed.clarifications);
        if (parsed.features && parsed.features.length > 0) setFeatures(parsed.features);
        if (parsed.designPatterns) setSelectedPatterns(parsed.designPatterns);
        if (parsed.entities) setEntities(parsed.entities);
        if (parsed.schemaEntities || parsed.entities) {
          const entitiesToUse = parsed.schemaEntities && parsed.schemaEntities.length > 0
            ? parsed.schemaEntities
            : (parsed.entities || []);
          setSchemaDesign({
            entities: entitiesToUse,
            relationships: parsed.schemaRelationships || []
          });
        }
        if (parsed.editorVisible !== undefined) setEditorVisible(parsed.editorVisible);
      } catch (e) {
        // If localStorage data is invalid, continue with API load
      }
    }

    // Load problem data from localStorage if available
    const problemRes = localStorage.getItem(`lld-problem-${problemId}`);
    
    if (problemRes) {
      try {
        const parsed = JSON.parse(problemRes);
        // Extract features from problemDetails if available
        const problemDetails = parsed.problemDetails || parsed;
        const problemFeatures = problemDetails.features || parsed.features || [];
        if (problemFeatures.length > 0) {
          const formattedFeatures = problemFeatures.map(f => 
            typeof f === 'string' ? f : f.text || f
          );
          setFeatures(formattedFeatures);
        }
      } catch (e) {
        // Continue with API load
      }
    }

    // Don't set loading to false here - keep it true until backend responds
    // The loadAllData function will set loading to false when it completes

    // Load from backend to sync
    loadAllData();
  }, [problemId, isAuthenticated]);

  // Extract features from problem data (similar to HLD)
  useEffect(() => {
    if (problem) {
      // Extract features from problem data
      const problemDetails = problem.problemDetails || problem;
      const problemFeatures = problemDetails.features || problem.features || [];
      
      // Set features from problem data if available
      if (problemFeatures.length > 0) {
        // Convert to array format if needed
        const formattedFeatures = problemFeatures.map(f => 
          typeof f === 'string' ? f : f.text || f
        );
        setFeatures(formattedFeatures);
        
        // Save features to attempt localStorage so document can access them
        if (problemId) {
          const attemptKey = `lld-attempt-${problemId}`;
          const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          currentAttemptData.features = formattedFeatures;
          currentAttemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
        }
      }
      
      // Store in localStorage for other components to access
      localStorage.setItem('lld-problem-data', JSON.stringify({
        features: problemFeatures
      }));
    }
  }, [problem]);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  // Add global mouse event listeners for right sidebar resizing
  useEffect(() => {
    if (isResizingRight) {
      document.addEventListener('mousemove', handleRightSidebarMouseMove);
      document.addEventListener('mouseup', handleRightSidebarMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleRightSidebarMouseMove);
        document.removeEventListener('mouseup', handleRightSidebarMouseUp);
      };
    }
  }, [isResizingRight]);

  if(loading) return <div style={{ marginTop: '100px' }}><Loader /></div>;
  // Only show "Problem not found" if loading is complete and problem is still null
  if(!loading && !problem) return <div style={{textAlign:'center',marginTop:'30px'}}>Problem not found</div>;

  // Stepper UI
  const Stepper = () => {
    // Check for unsaved changes in the code editor and diagrams
    const hasUnsavedChanges = (() => {
      if (step === 3) {
        const editorState = JSON.parse(localStorage.getItem(`lld-editor-files-${problemId}`) || '{}');
        // Check if there are unsaved changes and if the code was recently run (which would have saved it)
        const lastRunTime = localStorage.getItem(`lld-last-run-${problemId}`);
        const currentTime = Date.now();
        const recentlyRun = lastRunTime && (currentTime - parseInt(lastRunTime)) < 60000; // Within 1 minute
        
        return editorState.hasUnsavedChanges && !recentlyRun;
      }
      if (step === 1) {
        // Check for unsaved changes in either entities or class diagram
        const attemptData = JSON.parse(localStorage.getItem(`lld-attempt-${problemId}`) || '{}');
        const entitiesUnsaved = attemptData.schemaHasUnsavedChanges || false;
        const classDiagramUnsaved = attemptData.classDiagram?.hasUnsavedChanges || false;
        return entitiesUnsaved || classDiagramUnsaved;
      }
      return false;
    })();

    return (
    <div className="lld-stepper">
      {STEPS.map((s, idx) => (
        <React.Fragment key={s.label}>
          <div
            className={`lld-stepper-step${step === idx ? ' active' : ''}${step > idx ? ' completed' : ''}`}
            onClick={() => {
              setStepAndSave(idx);
            }}
            style={{ cursor: 'pointer' }}
          >
              <span className="lld-stepper-label">
                {s.label}
              </span>
          </div>
          {idx < STEPS.length - 1 && <div className="lld-stepper-arrow">→</div>}
        </React.Fragment>
      ))}
    </div>
  );
  };

  // Step Content
  let stepContent = null;
  const currentStepName = STEPS[step]?.label;
  
  if (step === 0) {
    stepContent = (
      <div className="fade-in">
        <Features
          features={features}
          problemTitle={problem?.title || ''}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
        />
      </div>
    );
  } else if (step === 1) {
    stepContent = (
      <div className="fade-in" style={{height:'calc(100vh - 64px)',width:'100%'}}>
        <CombinedDiagramDraw 
          problemId={problemId} 
          schemaDesign={schemaDesign}
          setSchemaDesign={setSchemaDesignAndUpdate}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
          onUnsavedChangesChange={setDiagramUnsavedChanges}
        />
      </div>
    );
  } else if (step === 2) {
    stepContent = (
      <div className="fade-in">
        <DesignPatternQuestions
          key={`lld-design-patterns-${problemId}`}
          selectedPatterns={selectedPatterns}
          onPatternsChange={handlePatternsChange}
          problemTitle={problem?.title || ''}
          features={features}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
        />
      </div>
    );
  } else if (step === 3) {
    // Set the problemId in localStorage so EditorProvider can access it
    if (problemId) {
      localStorage.setItem('current-lld-problemId', problemId);
    }
    
    stepContent = (
      <div className="fade-in" style={{height:'calc(100vh - 64px)',width:'100%', display: 'flex', flexDirection: 'column'}}>
        {/* Evaluate Button */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 1.5,
          py: 0.5,
          minHeight: '40px',
          gap: 1
        }}>
          <button
            onClick={handleCodeEvaluate}
            disabled={isEvaluatingCode}
            style={{
              backgroundColor: isEvaluatingCode ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: isEvaluatingCode ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
              transition: 'background-color 0.2s',
              minWidth: '120px'
            }}
            onMouseEnter={(e) => {
              if (!isEvaluatingCode) {
                e.target.style.backgroundColor = '#1565c0';
              }
            }}
            onMouseLeave={(e) => {
              if (!isEvaluatingCode) {
                e.target.style.backgroundColor = '#1976d2';
              }
            }}
          >
            {isEvaluatingCode ? 'Evaluating...' : 'Evaluate Code'}
          </button>
        </Box>
        
        {/* Editor and Feedback Panel */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <EditorProvider>
              <div className="app-container1" style={{ height: '100%' }}>
                <aside className="sidebar">
                  <Explorer />
                </aside>
                <main className="main-area">
                  <TabsBar />
                  <EditorPane />
                  <OutputPanel />
                </main>
              </div>
            </EditorProvider>
          </Box>
          
          {/* Evaluation Feedback Panel */}
          {codeEvaluationFeedback && (
            <Box sx={{ 
              width: '400px', 
              borderLeft: 1, 
              borderColor: 'divider',
              backgroundColor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden'
            }}>
              <EvaluationFeedbackPanel
                feedback={codeEvaluationFeedback}
                onClose={() => setCodeEvaluationFeedback(null)}
              />
            </Box>
          )}
        </Box>
      </div>
    );
  }

  // Set the problemId in localStorage for LLDDocument to access
  if (problemId) {
    localStorage.setItem('current-lld-problemId', problemId);
  }

  // Sidebar resize handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setSidebarWidth(newWidth);
      localStorage.setItem(`lld-sidebar-width-${problemId}`, newWidth.toString());
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Right sidebar resize handlers
  const handleRightSidebarMouseDown = (e) => {
    e.preventDefault();
    setIsResizingRight(true);
  };

  const handleRightSidebarMouseMove = (e) => {
    if (!isResizingRight) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 250;
    const maxWidth = 600;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setRightSidebarWidth(newWidth);
      localStorage.setItem(`lld-right-sidebar-width-${problemId}`, newWidth.toString());
    }
  };

  const handleRightSidebarMouseUp = () => {
    setIsResizingRight(false);
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    const newVisibility = !sidebarVisible;
    setSidebarVisible(newVisibility);
    localStorage.setItem(`lld-sidebar-visible-${problemId}`, JSON.stringify(newVisibility));
  };

  // Toggle right sidebar visibility
  const toggleRightSidebar = () => {
    const newVisibility = !rightSidebarVisible;
    setRightSidebarVisible(newVisibility);
    localStorage.setItem(`lld-right-sidebar-visible-${problemId}`, JSON.stringify(newVisibility));
  };

  const getStepColor = (stepIndex) => {
    if (step === stepIndex) return '#008080';
    if (step > stepIndex) return '#4caf50';
    return '#9e9e9e';
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Steps or Document */}
      {sidebarVisible && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            bgcolor: '#fafafa',
            borderRight: '1px solid #e0e0e0',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: isResizing ? 'none' : 'width 0.2s ease'
          }}
        >
          {/* Header Section */}
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mb: 0 }}>
              <IconButton onClick={() => navigate('/lld')} size="small">
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                {documentVisible ? 'LLD Document' : problem.title}
              </Typography>
            </Box>
          </Box>

          {/* Content Section - Steps or Document */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {documentVisible ? (
              // Document View
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                <LLDDocument />
              </Box>
            ) : (
              // Steps View
              <List sx={{ 
                pt: 0.5, 
                overflow: 'auto', 
                flexGrow: 1,
                // Hide scrollbars but maintain scrolling functionality
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                '-ms-overflow-style': 'none',  // IE and Edge
                'scrollbarWidth': 'none',      // Firefox
              }}>
                {STEPS.map((stepItem, index) => {
                  const isActive = step === index;
                  
                  return (
                    <ListItem
                      key={stepItem.label}
                      button
                      onClick={() => setStepAndSave(index)}
                      sx={{
                        mx: 1,
                        mb: 0.5,
                        borderRadius: 2,
                        bgcolor: 'transparent',
                        '&:hover': {
                          bgcolor: '#f5f5f5'
                        },
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                      }}
                    >
                      <ListItemText
                        primary={stepItem.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: isActive ? 'bold' : 'normal',
                            color: isActive ? '#008080' : '#333'
                          }
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
          
          {/* Resize Handle */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#008080',
                opacity: 0.3
              },
              '&:active': {
                backgroundColor: '#008080',
                opacity: 0.5
              }
            }}
            onMouseDown={handleMouseDown}
          />
        </Box>
      )}

      {/* Left Sidebar Toggle Button */}
      <Box
        sx={{
          position: 'absolute',
          left: sidebarVisible ? sidebarWidth -13 : 0,
          top: '13%',
          zIndex: 1001,
          transition: 'left 0.2s ease',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* Arrow Icon on the Line */}
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            color: '#008080',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#008080',
              color: 'white',
              borderColor: '#008080',
              transform: 'scale(1.1)'
            }
          }}
          onClick={toggleSidebar}
        >
          {sidebarVisible ? <ChevronLeft fontSize="inherit" /> : <ChevronRight fontSize="inherit" />}
        </Box>
      </Box>

      {/* Main Workspace Area */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        '& *': {
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          '-ms-overflow-style': 'none',
          'scrollbarWidth': 'none'
        }
      }}>
        {/* Content Area */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {stepContent}
        </Box>
      </Box>

      {/* Right Sidebar Toggle Button */}
      <Box
        sx={{
          position: 'absolute',
          right: rightSidebarVisible ? rightSidebarWidth -13 : 0,
          top: '13%',
          zIndex: 1001,
          transition: 'right 0.2s ease',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {/* Arrow Icon on the Line */}
        <Box
          sx={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '50%',
            color: '#008080',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#008080',
              color: 'white',
              borderColor: '#008080',
              transform: 'scale(1.1)'
            }
          }}
          onClick={toggleRightSidebar}
        >
          {rightSidebarVisible ? <ChevronRight fontSize="inherit" /> : <ChevronLeft fontSize="inherit" />}
        </Box>
      </Box>

      {/* Right Sidebar - Clarification Chat */}
      {rightSidebarVisible && (
        <Box
          sx={{
            width: rightSidebarWidth,
            flexShrink: 0,
            bgcolor: '#fafafa',
            borderLeft: '1px solid #e0e0e0',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            transition: isResizingRight ? 'none' : 'width 0.2s ease'
          }}
        >
          {/* Clarification Header */}
          <Box sx={{ 
            p: 1.8, 
            flexShrink: 0,
            bgcolor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 'bold', 
              color: '#333',
              fontSize: '1.1rem',
              lineHeight: 1.2
            }}>
              {UI_TEXT.TITLES.CLARIFICATIONS}
            </Typography>
          </Box>

          {/* Clarification Panel */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2 }}>
            <ClarificationPanel 
              problemId={problemId}
              problemTitle={problem?.title || ''}
              componentType="LLD"
              onClarificationsChange={handleClarificationsChange}
              initialConversation={clarifications}
            />
          </Box>
          
          {/* Resize Handle */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#008080',
                opacity: 0.3
              },
              '&:active': {
                backgroundColor: '#008080',
                opacity: 0.5
              }
            }}
            onMouseDown={handleRightSidebarMouseDown}
          />
        </Box>
      )}

      {/* Draggable Notes Component - Persistent across all steps */}
      {problemId && problem && <DraggableNotes problemId={problemId} problemType="LLD" problem={problem} />}

    </Box>
  );
} 